import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Loader2, Check } from 'lucide-react'

interface TenantManifest {
  id: string
  name: string
  operator: string
  branding: { primary: string }
  features: Record<string, boolean>
}

const FLAG_LABELS: Record<string, { label: string; description: string }> = {
  write_tools: {
    label: 'Write Tools',
    description: 'Allow the copilot agent to make changes via the MagicMoney API (edit events, plans, attractions).',
  },
  smart_import: {
    label: 'Smart Import',
    description: 'Enable CSV/Excel import wizard for bulk data uploads.',
  },
  transit_matrix: {
    label: 'Transit Matrix',
    description: 'Derive carnival layout from scan data for proximity and transit analytics.',
  },
  suggestions_synthesized: {
    label: 'Synthesized Suggestions',
    description: 'Use AI to generate contextual chat suggestions based on the operator\'s data.',
  },
  dashboard_pg: {
    label: 'PG Dashboard',
    description: 'Use the Postgres analytical cache for dashboard data instead of live MSSQL queries.',
  },
}

export function FeatureFlagsPage() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const [tenant, setTenant] = useState<TenantManifest | null>(null)
  const [features, setFeatures] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!tenantId) return
    fetch(`/api/portal/tenants/${tenantId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.tenant) {
          setTenant(data.tenant)
          setFeatures(data.tenant.features || {})
        }
      })
      .finally(() => setLoading(false))
  }, [tenantId])

  const toggleFlag = (key: string) => {
    setFeatures(prev => ({ ...prev, [key]: !prev[key] }))
    setSaved(false)
  }

  const saveFeatures = async () => {
    if (!tenantId) return
    setSaving(true)
    try {
      const res = await fetch(`/api/portal/tenants/${tenantId}/features`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ features }),
      })
      if (res.ok) {
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="p-6">
        <p className="text-ink-soft">Tenant not found.</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to={`/portal/tenants/${tenant.id}`}
          className="text-ink-soft hover:text-ink-dark transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-ink-dark">Feature Flags</h1>
          <p className="text-sm text-ink-soft">{tenant.name} — {tenant.operator}</p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 divide-y divide-slate-100">
        {Object.entries(FLAG_LABELS).map(([key, meta]) => (
          <div key={key} className="flex items-center justify-between p-4">
            <div className="flex-1 min-w-0 mr-4">
              <div className="text-sm font-medium text-ink-dark">{meta.label}</div>
              <div className="text-xs text-ink-soft mt-0.5">{meta.description}</div>
            </div>
            <button
              type="button"
              onClick={() => toggleFlag(key)}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                features[key] ? 'bg-green-500' : 'bg-slate-300'
              }`}
            >
              <span
                className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                  features[key] ? 'translate-x-[22px]' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={saveFeatures}
          disabled={saving}
          className="inline-flex items-center gap-2 text-sm font-medium px-5 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Save Changes
        </button>
        {saved && (
          <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
            <Check className="w-4 h-4" />
            Saved
          </span>
        )}
      </div>
    </div>
  )
}
